'use client'

import type { ChartSeries } from '@/components/charts/chart-parts'

import { useId } from 'react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import {
    Area,
    AreaChart,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts'

import {
    AxisLabel,
    CampaignLabel,
    SERIES,
    ValueLabel,
    seriesGradient
} from '@/components/charts/chart-parts'

/*
 * GoodUnited / Chart plots. Figma: 12 / Charts / Chart plots.
 * AreaPlot and LinePlot draw with recharts; StackedBarPlot is drawn with layout so campaign bands,
 * value labels and totals line up exactly with the columns.
 */

const useChartId = () => useId().replace(/[^a-zA-Z0-9]/g, '')

const axisTick = { fill: 'var(--gu-chart-axis-text)', fontSize: 14 }

interface TooltipEntry {
    name?: unknown
    value?: unknown
    dataKey?: unknown
}

interface ChartTooltipContentProps {
    active?: boolean
    label?: unknown
    payload?: ReadonlyArray<TooltipEntry>
    series?: Record<string, ChartSeries | 'positive'>
    labels?: Record<string, string>
    format?: (value: number) => string
}

const swatchFor = (series?: ChartSeries | 'positive') => series === 'positive' || !series
    ? 'var(--gu-chart-positive-start)'
    : SERIES[series].start

/* Chart tooltip: Colors/surface/overlay, 8px corners, Roboto / text-sm. */
export function ChartTooltipContent({ active, label, payload, series = {}, labels = {}, format = value => value.toLocaleString('en-US') }: ChartTooltipContentProps) {
    if (!active || !payload?.length) return null

    return (
        <div className='flex min-w-40 flex-col gap-2 rounded-lg border border-border-default bg-surface-overlay px-3 py-2.5 text-sm text-text-primary shadow-popover'>
            {label !== undefined && (
                <span className='font-medium'>
                    {String(label)}
                </span>
            )}
            {payload.map(entry => {
                const key = String(entry.dataKey ?? entry.name ?? '')
                const value = typeof entry.value === 'number' ? entry.value : Number(entry.value)

                return (
                    <span key={key} className='flex items-center justify-between gap-4'>
                        <span className='flex items-center gap-2 text-text-secondary'>
                            <span className='size-2 rounded-full' style={{ background: swatchFor(series[key]) }} aria-hidden='true' />
                            {labels[key] ?? key}
                        </span>
                        <span className='font-medium tabular-nums'>
                            {Number.isFinite(value) ? format(value) : String(entry.value)}
                        </span>
                    </span>
                )
            })}
        </div>
    )
}

type Datum = Record<string, string | number>

interface AreaPlotProps {
    data: Datum[]
    xKey: string
    yKey: string
    label?: string
    height?: number
    showXAxis?: boolean
    variant?: 'area' | 'line'
    className?: string
}

/* Chart/AreaPlot and the line-only variant used by Chart/Line/Compact. Colors/chart/positive. */
export function AreaPlot({ data, xKey, yKey, label = 'Value', height = 120, showXAxis = false, variant = 'area', className }: AreaPlotProps) {
    const id = useChartId()
    const values = data.map(datum => Number(datum[yKey]))
    const min = Math.min(...values)
    const max = Math.max(...values)
    const pad = (max - min) * 0.1 || 1

    return (
        <div className={cn('w-full', className)} style={{ height }}>
            <ResponsiveContainer width='100%' height='100%'>
                <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
                    <defs>
                        <linearGradient id={`${id}-fill`} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='var(--gu-chart-positive-start)' stopOpacity={0.35} />
                            <stop offset='100%' stopColor='var(--gu-chart-positive-end)' stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <XAxis dataKey={xKey} hide={!showXAxis} tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis hide domain={[min - pad, max + pad]} />
                    <RechartsTooltip
                        cursor={{ stroke: 'var(--gu-chart-axis-line)' }}
                        content={props => <ChartTooltipContent active={props.active} label={props.label} payload={props.payload} series={{ [yKey]: 'positive' }} labels={{ [yKey]: label }} />}
                    />
                    <Area
                        type='monotone'
                        dataKey={yKey}
                        stroke='var(--gu-chart-positive-start)'
                        strokeWidth={1.5}
                        fill={variant === 'area' ? `url(#${id}-fill)` : 'transparent'}
                        activeDot={{ r: 4, fill: 'var(--gu-chart-positive-start)', stroke: 'var(--gu-surface-raised)', strokeWidth: 2 }}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

interface LineSpec {
    key: string
    label: string
    series: ChartSeries
}

interface LinePlotProps {
    data: Datum[]
    xKey: string
    lines: readonly LineSpec[]
    /* The first line can be drawn as a shaded total. */
    shadedKey?: string
    hidden?: string[]
    height?: number
    domain?: [number, number]
    ticks?: number[]
    className?: string
}

const formatThousands = (value: number) => value === 0 ? '0' : `${value / 1000}k`

/* Chart/LinePlot: channel lines, an optional shaded total, a zero baseline; negative series plot below zero. */
export function LinePlot({ data, xKey, lines, shadedKey, hidden = [], height = 320, domain, ticks, className }: LinePlotProps) {
    const id = useChartId()
    const seriesMap = Object.fromEntries(lines.map(line => [line.key, line.series]))
    const labelMap = Object.fromEntries(lines.map(line => [line.key, line.label]))
    const shaded = lines.find(line => line.key === shadedKey)

    return (
        <div className={cn('w-full', className)} style={{ height }}>
            <ResponsiveContainer width='100%' height='100%'>
                <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                        <linearGradient id={`${id}-total`} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='var(--gu-chart-misc-start)' stopOpacity={0.28} />
                            <stop offset='100%' stopColor='var(--gu-chart-misc-end)' stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke='var(--gu-chart-grid)' />
                    <XAxis dataKey={xKey} tick={axisTick} axisLine={false} tickLine={false} tickMargin={12} interval='preserveStartEnd' />
                    <YAxis width={40} tick={axisTick} axisLine={false} tickLine={false} domain={domain} ticks={ticks} tickFormatter={formatThousands} />
                    <ReferenceLine y={0} stroke='var(--gu-chart-axis-line)' />
                    <RechartsTooltip
                        cursor={{ stroke: 'var(--gu-chart-axis-line)' }}
                        content={props => <ChartTooltipContent active={props.active} label={props.label} payload={props.payload} series={seriesMap} labels={labelMap} />}
                    />
                    {shaded && (
                        <Area
                            type='monotone'
                            dataKey={shaded.key}
                            name={shaded.label}
                            hide={hidden.includes(shaded.key)}
                            stroke={SERIES[shaded.series].start}
                            strokeWidth={1.5}
                            fill={`url(#${id}-total)`}
                            dot={false}
                            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--gu-surface-raised)' }}
                            isAnimationActive={false}
                        />
                    )}
                    {lines.filter(line => line.key !== shadedKey).map(line => (
                        <Line
                            key={line.key}
                            type='monotone'
                            dataKey={line.key}
                            name={line.label}
                            hide={hidden.includes(line.key)}
                            stroke={SERIES[line.series].start}
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--gu-surface-raised)' }}
                            isAnimationActive={false}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

interface StackedBarKey {
    key: string
    label: string
    series: ChartSeries
}

interface StackedBarDatum {
    label: string
    values: Record<string, number>
}

interface CampaignBandSpec {
    label: string
    from: number
    to: number
    row?: number
}

interface StackedBarPlotProps {
    data: readonly StackedBarDatum[]
    keys: readonly StackedBarKey[]
    bands?: readonly CampaignBandSpec[]
    hidden?: string[]
    max?: number
    step?: number
    height?: number
    format?: (value: number) => string
    className?: string
}

const BAND_ROW = 40

const formatK = (value: number) => `$${Number.isInteger(value) ? value : value.toFixed(1)}k`

/*
 * Chart/BarSegment + Chart/StackedBar + Chart/CampaignBand, composed as a stacked column plot.
 * keys run bottom to top. Bands sit behind the columns; their labels stagger across rows.
 */
export function StackedBarPlot({ data, keys, bands = [], hidden = [], max = 80, step = 10, height = 280, format = formatK, className }: StackedBarPlotProps) {
    const rows = bands.length ? Math.max(...bands.map(band => band.row ?? 0)) + 1 : 0
    const top = rows * BAND_ROW
    const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, index) => index * step)
    const column = 100 / data.length
    const visible = keys.filter(item => !hidden.includes(item.key))

    return (
        <div className={cn('overflow-x-auto', className)}>
            <div className='flex min-w-215 gap-3'>
                <div className='relative w-10 shrink-0' style={{ height: top + height }} aria-hidden='true'>
                    {ticks.map(tick => (
                        <AxisLabel key={tick} className='absolute right-0 translate-y-1/2' style={{ bottom: tick / max * height }}>
                            {format(tick)}
                        </AxisLabel>
                    ))}
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                    <div className='relative' style={{ height: top + height }}>
                        {bands.map(band => (
                            <div
                                key={`${band.label}-${band.from}-bg`}
                                className='absolute bottom-0 px-1'
                                style={{ top: (band.row ?? 0) * BAND_ROW + 18, left: `${band.from * column}%`, width: `${(band.to - band.from + 1) * column}%` }}
                                aria-hidden='true'
                            >
                                <div className='h-full rounded-md bg-chart-highlight-band' />
                            </div>
                        ))}
                        {bands.map(band => (
                            <div
                                key={`${band.label}-${band.from}-label`}
                                className='absolute px-1'
                                style={{ top: (band.row ?? 0) * BAND_ROW, left: `${band.from * column}%`, width: `${(band.to - band.from + 1) * column}%` }}
                            >
                                <CampaignLabel label={band.label} aria-label={`${band.label} campaign, ${data[band.from]?.label} to ${data[band.to]?.label}`} />
                            </div>
                        ))}
                        <div className='absolute inset-x-0 bottom-0 grid' style={{ height, gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
                            {data.map(datum => {
                                const total = visible.reduce((sum, item) => sum + (datum.values[item.key] ?? 0), 0)

                                return (
                                    <Tooltip key={datum.label}>
                                        <TooltipTrigger
                                            className='group/bar relative flex h-full cursor-default flex-col items-center justify-end rounded-md px-1.5 outline-none focus-visible:outline-2 focus-visible:outline-border-focus'
                                            aria-label={`${datum.label}: ${format(total)} total`}
                                        >
                                            <span className='mb-1 text-xs font-medium text-text-primary'>
                                                {total > 0 ? format(total) : ''}
                                            </span>
                                            <span className='flex w-full max-w-14 flex-col-reverse gap-px transition-opacity group-hover/bar:opacity-90 [&>span:last-child]:rounded-t-md' style={{ height: `${total / max * 100}%` }}>
                                                {visible.map(item => {
                                                    const value = datum.values[item.key] ?? 0

                                                    if (!value) return null

                                                    return (
                                                        <span
                                                            key={item.key}
                                                            className='flex min-h-0 items-center justify-center'
                                                            style={{ height: `${value / total * 100}%`, backgroundImage: seriesGradient(item.series) }}
                                                        >
                                                            {value / max * height >= 18 && (
                                                                <ValueLabel tone={SERIES[item.series].label}>
                                                                    {format(value)}
                                                                </ValueLabel>
                                                            )}
                                                        </span>
                                                    )
                                                })}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side='top'>
                                            <div className='flex min-w-44 flex-col gap-2'>
                                                <span className='font-medium'>
                                                    {datum.label}
                                                </span>
                                                {[...visible].reverse().map(item => (
                                                    <span key={item.key} className='flex items-center justify-between gap-4'>
                                                        <span className='flex items-center gap-2 text-text-secondary'>
                                                            <span className='size-2 rounded-full' style={{ backgroundImage: seriesGradient(item.series) }} aria-hidden='true' />
                                                            {item.label}
                                                        </span>
                                                        <span className='font-medium tabular-nums'>
                                                            {format(datum.values[item.key] ?? 0)}
                                                        </span>
                                                    </span>
                                                ))}
                                                <span className='flex justify-between gap-4 border-t border-border-subtle pt-2 font-medium'>
                                                    Total
                                                    <span className='tabular-nums'>
                                                        {format(total)}
                                                    </span>
                                                </span>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                )
                            })}
                        </div>
                    </div>
                    <div className='grid' style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }} aria-hidden='true'>
                        {data.map(datum => (
                            <AxisLabel key={datum.label} className='truncate text-center text-xs'>
                                {datum.label}
                            </AxisLabel>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export type { CampaignBandSpec, LineSpec, StackedBarDatum, StackedBarKey }
